import { getMostRestrictiveSettingVisibility } from "./access";
import type {
  SettingAccess,
  SettingAgentEditability,
  SettingGroupDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  SettingRisk,
  SettingVisibility,
} from "./types";

export interface SettingMetadataDefaultsContext<
  SettingInput extends SettingRegistryMetadataInput =
    SettingRegistryMetadataInput,
  Group extends SettingGroupDefinition = SettingGroupDefinition,
> {
  setting: SettingInput;
  group: Group;
  access: SettingAccess;
  risk: SettingRisk;
  affectsBilling?: boolean;
}

export type SettingMetadataAgentEditabilityResolver<
  SettingInput extends SettingRegistryMetadataInput =
    SettingRegistryMetadataInput,
  Group extends SettingGroupDefinition = SettingGroupDefinition,
> = (
  context: SettingMetadataDefaultsContext<SettingInput, Group>,
) => SettingAgentEditability;

export function createSettingMetadataFromGroupDefaults<
  SettingInput extends SettingRegistryMetadataInput,
  Group extends SettingGroupDefinition,
>({
  setting,
  group,
  resolveAgentEditability = getDefaultSettingAgentEditability,
}: {
  setting: SettingInput;
  group: Group;
  resolveAgentEditability?: SettingMetadataAgentEditabilityResolver<
    SettingInput,
    Group
  >;
}): SettingMetadata {
  const owner = setting.owner ?? group.owner;
  const affectsBilling = setting.affectsBilling ?? group.affectsBilling;
  const access = createSettingAccess(setting, group);
  const risk = createSettingRisk(setting, group);

  return {
    label: setting.label,
    searchLabels: setting.searchLabels,
    description: setting.description,
    productArea: group.productArea,
    relatedGroups: setting.relatedGroups,
    access,
    owner,
    affectsBilling,
    synonyms: setting.synonyms,
    risk,
    agentEditability: resolveAgentEditability({
      setting,
      group,
      access,
      risk,
      affectsBilling,
    }),
    availability: setting.availability ?? group.availability,
    valueSemantics: setting.valueSemantics ?? group.valueSemantics,
    changeGuidance: setting.changeGuidance ?? group.changeGuidance,
    dependencies: mergeMetadataList(group.dependencies, setting.dependencies),
  };
}

export function getDefaultSettingAgentEditability({
  setting,
  group,
  access,
  risk,
  affectsBilling,
}: SettingMetadataDefaultsContext): SettingAgentEditability {
  if (setting.agentEditability) {
    return setting.agentEditability;
  }

  if (access.write.mode === "none") {
    return { mode: "never", reason: access.write.reason };
  }

  if (affectsBilling) {
    return {
      mode: "requiresApproval",
      reason: "This setting affects billing.",
    };
  }

  if (risk.level === "critical" || risk.level === "high") {
    return { mode: "requiresApproval", reason: risk.reason };
  }

  return group.agentEditability;
}

function createSettingAccess(
  setting: SettingRegistryMetadataInput,
  group: SettingGroupDefinition,
): SettingAccess {
  if (setting.access) {
    return setting.access;
  }

  if (!setting.visibility && !setting.permission) {
    return group.access;
  }

  const minVisibility = setting.visibility ?? group.access.read.minVisibility;

  return {
    read: { ...group.access.read, minVisibility },
    write: createWriteAccessForSetting(setting, group, minVisibility),
  };
}

function createWriteAccessForSetting(
  setting: SettingRegistryMetadataInput,
  group: SettingGroupDefinition,
  minVisibility: SettingVisibility,
): SettingAccess["write"] {
  if (group.access.write.mode !== "grants") {
    return group.access.write;
  }

  return {
    mode: "grants",
    grants: group.access.write.grants.map((grant) => ({
      ...grant,
      minVisibility: getMostRestrictiveSettingVisibility(
        grant.minVisibility,
        minVisibility,
      ),
      permission: setting.permission ?? grant.permission,
    })),
  };
}

function createSettingRisk(
  setting: SettingRegistryMetadataInput,
  group: SettingGroupDefinition,
): SettingRisk {
  if (!setting.risk) {
    return group.risk;
  }

  if (typeof setting.risk === "string") {
    return group.risk.reason
      ? { level: setting.risk, reason: group.risk.reason }
      : { level: setting.risk };
  }

  return setting.risk;
}

function mergeMetadataList<T>(
  groupValues: readonly T[] | undefined,
  settingValues: readonly T[] | undefined,
): readonly T[] | undefined {
  if (!groupValues?.length) {
    return settingValues;
  }

  if (!settingValues?.length) {
    return groupValues;
  }

  return [...groupValues, ...settingValues];
}
